import { UserIdentityStatusEnum } from "@/components/types/UserIdentity.Status";
import { isTransferPendingAcceptance } from "@/screen/Identities/IdentityForm/acceptTransferRules";
import type { Identity } from "@/screen/Identities/Interfaces/IdentitiesMainScreenInterfaces";
import { formatIsoDateTimeToDdMmYyyy } from "@/utils/DateFormatter";

/**
 * Etiqueta de la columna "Test de idoneidad".
 * El estado lo decide el backend. Si no llega, o llega un valor que no es uno de los cuatro, se usa
 * suitabilityTestCompleted y no hay botón de renovar.
 */
export function getSuitabilityTestLabel(identity: Pick<Identity, "suitabilityTestStatus" | "suitabilityTestCompleted">): string
{
    switch(identity.suitabilityTestStatus)
    {
        case "NOT_APPLICABLE":
            return "No aplica";
        case "IN_FORCE":
            return "Sí";
        case "EXPIRED":
        case "NONE":
            return "No";
        default:
            break;
    }

    if(identity.suitabilityTestCompleted === undefined)
    {
        return "-";
    }

    return identity.suitabilityTestCompleted ? "Sí" : "No";
}

export interface SuitabilityTestAction
{
    visible: boolean;
    disabled: boolean;
    title: string;
}

const HiddenSuitabilityTestAction: SuitabilityTestAction =
{
    visible: false,
    disabled: false,
    title: "",
};

/**
 * Botón de la columna. Sale con el test vigente y con el caducado.
 * canUpdateSuitabilityTest lo decide el backend: si es false y hay fecha, se ve deshabilitado.
 * Sin el campo (resumen antiguo) solo sale el caducado, como antes.
 * La pantalla sigue exigiendo propietario, adulto, no externo, sin cesión y fuera del modo contrato.
 */
export function getSuitabilityTestAction(identity: Identity, isContractMode: boolean): SuitabilityTestAction
{
    if(identity.suitabilityTestStatus !== "IN_FORCE" && identity.suitabilityTestStatus !== "EXPIRED")
    {
        return HiddenSuitabilityTestAction;
    }

    if(!passesSuitabilityScreenRules(identity, isContractMode))
    {
        return HiddenSuitabilityTestAction;
    }

    const title = identity.suitabilityTestStatus === "IN_FORCE"
        ? "Actualizar test de idoneidad"
        : "Renovar test de idoneidad";

    if(identity.canUpdateSuitabilityTest === false)
    {
        const formatted = formatIsoDateTimeToDdMmYyyy(identity.suitabilityTestNextUpdateAt);
        if(!identity.suitabilityTestNextUpdateAt || formatted === "—")
        {
            return HiddenSuitabilityTestAction;
        }

        return{
            visible: true,
            disabled: true,
            title: `Podrás actualizarlo a partir del ${formatted}`,
        };
    }

    if(identity.canUpdateSuitabilityTest !== true && identity.suitabilityTestStatus !== "EXPIRED")
    {
        return HiddenSuitabilityTestAction;
    }

    return{
        visible: true,
        disabled: false,
        title: title,
    };
}

/** El icono hace algo: está visible y no está bloqueado por los 30 días. */
export function canRenewSuitabilityTest(identity: Identity, isContractMode: boolean): boolean
{
    const action = getSuitabilityTestAction(identity, isContractMode);
    return action.visible && !action.disabled;
}

function passesSuitabilityScreenRules(identity: Identity, isContractMode: boolean): boolean
{
    if(identity.role !== "Owner" || isExternalIdentity(identity))
    {
        return false;
    }

    if(identity.isMinor)
    {
        return false;
    }

    if(identity.role === "Owner" && isTransferPendingAcceptance(identity.status))
    {
        return false;
    }

    if(isContractMode)
    {
        return false;
    }

    return true;
}

/**
 * EXTERNAL explícito, o la forma en que llega en el resumen: Status a null y CanEdit a false.
 * NO_ACTIVO comparte ese hueco, pero el backend no le asigna EXPIRED, y quien llama aquí ya lo exige.
 * No sustituye al isExternal de IdentitiesMainScreen, que solo mira EXTERNAL: un prestado (Guest) llega
 * siempre con CanEdit a false, y si su estado es NULL (legado) esta regla lo tomaría por externo y le
 * quitaría "Renunciar al acceso". Aquí no pasa porque canRenewSuitabilityTest descarta antes a los Guest.
 */
function isExternalIdentity(identity: Pick<Identity, "status" | "canEdit">): boolean
{
    if(identity.status === UserIdentityStatusEnum.External)
    {
        return true;
    }

    const statusMissing = identity.status == null || String(identity.status).trim() === "";
    return statusMissing && identity.canEdit === false;
}
